import React, { useCallback, useEffect } from 'react';
import { Prompt, useLocation } from 'react-router-dom';

// Post a Job and Post Prepaid save each step to a draft when the user moves on, so only the step
// on screen is lost.
export const UNPOSTED_LISTING_MESSAGE = 'Your job listing hasn\'t been posted yet. Leave this page? Changes on the current step will be lost.';

// Edit keeps changes in memory until Save.
export const UNSAVED_EDIT_MESSAGE = 'Your changes to this listing haven\'t been saved. Leave this page? They will be lost.';

// A dialog can approve one specific move. Keep the approval briefly in case the browser does
// not start navigating, but consume it on the first matching guard check. A route approval must
// not suppress a tab close, and an unload approval must not suppress an in-app move.
const ALLOW_WINDOW_MS = 1000;
type LeaveAllowance = { kind: 'unload' | 'route'; expiresAt: number; pathname?: string };
let leaveAllowance: LeaveAllowance | null = null;

const consumeLeaveAllowance = (kind: LeaveAllowance['kind'], pathname?: string): boolean => {
  if (!leaveAllowance) return false;
  if (Date.now() >= leaveAllowance.expiresAt) {
    leaveAllowance = null;
    return false;
  }
  if (leaveAllowance.kind !== kind || (kind === 'route' && leaveAllowance.pathname !== pathname)) return false;
  leaveAllowance = null;
  return true;
};

/** Approve the next document unload, or one in-app route. Call immediately before navigating. */
export const allowLeavingPage = (route?: string) => {
  leaveAllowance = route
    ? { kind: 'route', pathname: new URL(route, window.location.href).pathname, expiresAt: Date.now() + ALLOW_WINDOW_MS }
    : { kind: 'unload', expiresAt: Date.now() + ALLOW_WINDOW_MS };
};

interface ILeavePageGuardProps {
  when: boolean;
  message: string;
}

// Asks before the user leaves a page with work they would lose. Prompt covers moves inside the
// app, including the browser Back button between app pages, and shows `message`. beforeunload
// covers leaving the app (Back to another site, reload, closing the tab); there the browser shows
// its own wording and ignores ours.
export const LeavePageGuard = ({ when, message }: ILeavePageGuardProps) => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!when) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      if (consumeLeaveAllowance('unload')) return;
      event.preventDefault();
      // Older Chrome and Safari only prompt when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [when]);

  // Stable, because Prompt drops and re-registers its history block whenever `message` changes.
  // A query-string change on the same page is not leaving it.
  const confirmLeaving = useCallback(
    (location: { pathname: string }) => location.pathname === pathname || consumeLeaveAllowance('route', location.pathname) || message,
    [pathname, message]
  );

  return (
    <Prompt
      when={when}
      message={confirmLeaving}
    />
  );
};
